import useQueryParams from "Hooks/useQueryParams";
import { CONSTANTS } from "consts";
import React, { useEffect, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import { useGetSubscriptionData } from "./RedeemOffer.hooks";
import { AvironLoading } from "Components/Icons/AvironLoading";
import { Box, Button, Typography } from "@mui/material";
import { Helmet } from "react-helmet";
import GoBack from "Components/GoBack";
import {
    GetAvironSubscriptionQuery,
    useClaimCancelOfferSubscriptionMutation,
} from "services/webServer";
import MembershipRedeemOfferModal, {
    IMembershipRedeemOfferModalRef,
} from "./MembershipRedeemOfferModal";
import moment from "moment";
import { MembershipType, useTrackingData } from "Components/TrackingData";
import posthog from "posthog-js";
import { useCheckIsABTest } from "Hooks/useCheckIsABTest";
import { isInteger } from "lodash";
import { handleCheckIsAnnualMembership, handleCheckIsMonthlyMembership } from "Screens/Memberships/utils/Membership.utils";

enum SUBSCRIPTION_TYPE {
    MONTHLY = "monthly",
    ANNUAL = "yearly",
}

interface IMembershipOffer {
    id: string;
    name: string;
    originalPrice: number;
    discountedPrice: number;
    type: string;
    productId: string;
    intervalCount: number;
}

const RedeemOffer = () => {
    const { typeChoosing, isWarranty = "" } = useQueryParams();
    // const isABTestCancelFollow = useCheckIsABTest(
    //     "membership-cancellation-follow"
    // );

    const isABTestCancelFollow = true;
    const isABTestCancelFlowAnnual = useCheckIsABTest("cancellation-flow-annual")

    const membershipRedeemOfferSuccesRef =
        useRef<IMembershipRedeemOfferModalRef>(null);
    const history = useHistory();
    const { id = "" } = useQueryParams();
    const [data, setData] = React.useState<IMembershipOffer>(
        {} as IMembershipOffer
    );

    const { subscriptionData, isLoadingSubscription, error } =
        useGetSubscriptionData(id);
    const [claimCancelOffer] = useClaimCancelOfferSubscriptionMutation({
        onError: async (error) => {
             history.push(
                 CONSTANTS.NAVIGATION_URL.MEMBERSHIP_DETAIL.replace(
                     ":id",
                     id || ""
                 )
             );
        },
        onCompleted: () => {
            membershipRedeemOfferSuccesRef.current?.setIsVisible(true);
        },
    });
    const { trackngOfferConfirmScreenView } = useTrackingData(id);

    const handleRenderErrorContent = () => {
        return (
            <>
                {Swal.fire("Error", "Something went wrong", "error").then(
                    (result) => {
                        if (result.isConfirmed) {
                            history.replace(
                                CONSTANTS.NAVIGATION_URL.MEMBERSHIPS
                            );
                        }
                    }
                )}
                ;
            </>
        );
    };

    const MEMBERSHIP_OFFER_OPTIONS: IMembershipOffer[] = useMemo(() => {
        const isUSDCurrency =
            subscriptionData?.avironSubscription?.plan?.currency === "usd";
        const isGoMembership =
            subscriptionData?.avironSubscription?.plan?.appIds?.includes(16);
        return [
            {
                id: "1",
                name: `Annual Family Membership With 20-Year Warranty ${isGoMembership ? "Go": ""}`,
                originalPrice: isUSDCurrency ? 348 : 456,
                discountedPrice: 0.75,
                type: SUBSCRIPTION_TYPE.ANNUAL,
                productId: "membership",
                intervalCount: 1,
            },
            {
                id: "2",
                name: `Monthly Family Membership With 20-Year Warranty ${isGoMembership ? "Go": ""}`,
                originalPrice: isUSDCurrency ? 34 : 44,
                discountedPrice: 0.5,
                type: SUBSCRIPTION_TYPE.MONTHLY,
                productId: "membership",
                intervalCount: 2,
            }
        ];
    }, [subscriptionData]);

    const isChoosingAnotherPlan = useMemo(() => {
        if (
            !isABTestCancelFollow ||
            Boolean(!subscriptionData) ||
            Boolean(!typeChoosing)
        )
            return false;
        const currentSubscriptionPlan =
            subscriptionData?.avironSubscription?.plan?.interval;
        return isABTestCancelFollow && typeChoosing !== currentSubscriptionPlan;
    }, [isABTestCancelFollow, subscriptionData]);

    const isMonthlyMembership = useMemo(() => {
        if (!data) return false;
        return handleCheckIsMonthlyMembership(data?.type)
    }, [data]);

    const isAnnualMembership = useMemo(() => {
        if (!data) return false;
        return handleCheckIsAnnualMembership(data?.type)
    }, [data]);

    const handleRedeemOffer = async () => {
        if (!id) return;
        Swal.showLoading();

        if (isABTestCancelFollow && isChoosingAnotherPlan) {
            await claimCancelOffer({
                variables: {
                    id,
                    isChoosingUpgradePlan: true,
                },
            });
        } else {
            await claimCancelOffer({
                variables: {
                    id,
                }
            });
            if(isAnnualMembership) {
              posthog.capture("web_annual_cancel_claim_offer", {
                  offer_name: "25% off for a year",
                  variant: isABTestCancelFlowAnnual ? "test" : "control"
              })
            }
        }
        Swal.close();
    };

    const handleCalculateDiscountPrice = (type: string): number => {
        switch (type) {
            case SUBSCRIPTION_TYPE.MONTHLY:
                return 0.5;
            case SUBSCRIPTION_TYPE.ANNUAL:
                return 0.75;
            default:
                return 0;
        }
    };

        const handleForrmatMembershipType = (type: MembershipType) => {
            switch (type) {
                case "monthly":
                    return "annual"
                case "annual":
                    return "monthly"  
                default:
                    return "monthly"
            }
        }

    useEffect(() => {
        if (subscriptionData) {
            trackngOfferConfirmScreenView(
                subscriptionData,
                isChoosingAnotherPlan
                    ? handleForrmatMembershipType(
                          subscriptionData.avironSubscription?.plan
                              ?.interval as MembershipType
                      )
                    : (subscriptionData.avironSubscription?.plan
                          ?.interval as MembershipType)
            );
        }
        if (subscriptionData?.avironSubscription?.remark?.cancel_offer) {
            history.replace(`${CONSTANTS.NAVIGATION_URL.MEMBERSHIPS}/${id}`);
        }
    }, [subscriptionData, isChoosingAnotherPlan]);

    useEffect(() => {
        if (Boolean(!subscriptionData)) return;
        const isNotExistWarranty = subscriptionData?.avironSubscription?.plan?.productId === "membership";
        if (isChoosingAnotherPlan || isWarranty === "true") {
            let dataFormatted = MEMBERSHIP_OFFER_OPTIONS.find(
                (item) =>
                    item.type === (typeChoosing || subscriptionData?.avironSubscription?.plan?.interval)
            ) as IMembershipOffer;
            if(isNotExistWarranty) {
                dataFormatted = {
                    ...dataFormatted,
                    name: "Annual Family Membership",
                }
            }
            setData(dataFormatted as IMembershipOffer);
        } else {
                const isUSDCurrency =
                    subscriptionData?.avironSubscription?.plan?.currency === "usd";
                const isTwoYears = subscriptionData?.avironSubscription?.plan?.intervalCount === 2;
                const membershipType =
                    subscriptionData?.avironSubscription?.plan?.interval || "";
                const isAnnual = membershipType === SUBSCRIPTION_TYPE.ANNUAL;
                let newOriginalPrice = isAnnual
                    ? MEMBERSHIP_OFFER_OPTIONS[0].originalPrice : MEMBERSHIP_OFFER_OPTIONS[1].originalPrice
                if(isTwoYears) {
                    newOriginalPrice = isUSDCurrency ? 696 : 912;
                }
                const dataFormatted = {
                    id: Math.random().toString(26).substr(2, 4),
                    name: subscriptionData?.avironSubscription?.plan?.name || "",
                    originalPrice: newOriginalPrice,
                    discountedPrice: handleCalculateDiscountPrice(
                        subscriptionData?.avironSubscription?.plan
                            ?.interval as string
                    ) as number,
                    type: membershipType,
                    productId:
                        subscriptionData?.avironSubscription?.plan?.productId || "",
                    intervalCount:
                        membershipType === SUBSCRIPTION_TYPE.ANNUAL ? 1 : 2,
                };
                setData(dataFormatted);       
        }
    }, [isChoosingAnotherPlan, subscriptionData]);

    if (isLoadingSubscription) return <AvironLoading />;

    if (Boolean(error)) {
        handleRenderErrorContent();
    }

    return (
        <Box
            sx={{
                marginTop: {
                    xs: "24px",
                    md: "0",
                },
                marginBottom: {
                    xs: "24px",
                    md: "0px",
                },
                paddingLeft: {
                    xs: "12px",
                    md: "0",
                },
                paddingRight: {
                    xs: "12px",
                    md: "0",
                },
            }}
        >
            <Helmet>
                <meta charSet="utf-8" />
                <title>Membership Review And Cancel | Aviron</title>
                <link rel="canonical" href={window.location.href} />
            </Helmet>
            <GoBack />
            <Box
                sx={{
                    padding: {
                        xs: "16px 12px",
                        sm: "40px",
                    },
                    backgroundColor: "#fff",
                    marginTop: "24px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    height: {
                        xs: "60vh",
                        md: "auto",
                    },
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: {
                                xs: "18px",
                                md: "24px",
                            },
                            lineHeight: {
                                xs: "20px",
                                md: "25.08px",
                            },
                            letterSpacing: "0.01em",
                            marginBottom: "24px",
                            fontFamily: "GRIFTER",
                        }}
                    >
                        REDEEM YOUR OFFER
                    </Typography>
                    <Box
                        sx={{
                            backgroundColor: "#f4f4f4",
                            display: "flex",
                            alignContent: "center",
                            justifyContent: "space-between",
                            padding: {
                                xs: "12px",
                                md: "24px",
                            },
                            marginBottom: {
                                xs: "24px",
                                md: "40px",
                            },
                            flexDirection: {
                                xs: "column",
                                md: "row",
                            },
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontFamily: "Work Sans",
                                fontSize: {
                                    xs: "16px",
                                    md: "20px",
                                },
                                lineHeight: {
                                    xs: "20px",
                                    md: "24px",
                                },

                                textTransform: "uppercase",
                                marginBottom: {
                                    xs: "12px",
                                    md: "0",
                                },
                            }}
                        >
                            {data?.name}
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: "MonoSpec",
                                    fontWeight: 500,
                                    fontSize: {
                                        xs: "20px",
                                        md: "24px",
                                    },
                                    lineHeight: {
                                        xs: "24.98px",
                                        md: "29.98px",
                                    },
                                    color: "#808080",
                                    textDecoration: "line-through",
                                }}
                            >
                                ${Number(data?.originalPrice)}
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: "MonoSpec",
                                    fontWeight: 500,
                                    fontSize: {
                                        xs: "20px",
                                        md: "24px",
                                    },
                                    lineHeight: {
                                        xs: "24.98px",
                                        md: "29.98px",
                                    },
                                    color: "#161616",
                                    marginLeft: "24px",
                                }}
                            >
                                $
                                {isInteger(
                                    data?.originalPrice * data?.discountedPrice
                                )
                                    ? Number(
                                          data?.originalPrice *
                                              data?.discountedPrice
                                      )
                                    : Number(
                                          data?.originalPrice *
                                              data?.discountedPrice
                                      ).toFixed(2)}
                            </Typography>
                        </Box>
                    </Box>
                    <Typography
                        sx={{
                            fontWeight: 400,
                            fontSize: {
                                xs: "16px",
                                md: "18px",
                            },
                            lineHeight: {
                                xs: "20px",
                                md: "21.11px",
                            },
                            color: "#161616",
                            marginBottom: {
                                xs: "0",
                                md: "40px",
                            },
                        }}
                    >
                        {isChoosingAnotherPlan &&
                        data?.type === SUBSCRIPTION_TYPE.ANNUAL ? (
                            <>
                                Once you click the button below, you’ll be
                                charged a discounted annual membership fee,
                                adjusted to subtract what you’ve already paid
                                for this month. After that, the regular annual
                                membership fee will apply.
                            </>
                        ) : isMonthlyMembership ? (
                            <>
                                Starting on{" "}
                                <span style={{ fontWeight: 500 }}>
                                    {moment(
                                        subscriptionData?.avironSubscription
                                            ?.currentPeriodEnd || new Date()
                                    )
                                        .add(1, "day")
                                        .format("MMMM Do, YYYY")}
                                </span>
                                , you'll be charged a promotional price of
                                <span style={{ fontWeight: 500 }}>
                                    {" "}
                                    $
                                    {isInteger(
                                        data?.originalPrice *
                                            data?.discountedPrice
                                    )
                                        ? Number(
                                              data?.originalPrice *
                                                  data?.discountedPrice
                                          )
                                        : Number(
                                              data?.originalPrice *
                                                  data?.discountedPrice
                                          ).toFixed(2)}
                                </span>{" "}
                                for 2 months. After that, the regular monthly
                                membership fee will apply.
                            </>
                        ) : (
                            <>
                                Starting on{" "}
                                <span
                                    style={{
                                        fontWeight: 500,
                                    }}
                                >
                                    {moment(
                                        subscriptionData?.avironSubscription
                                            ?.currentPeriodEnd || new Date()
                                    )
                                        .add(1, "day")
                                        .format("MMMM Do, YYYY")}
                                </span>
                                , you'll be charged a promotional price of{" "}
                                <span style={{ fontWeight: 500 }}>
                                    {" "}
                                    $
                                    {Number(
                                        data?.originalPrice *
                                            data?.discountedPrice *
                                            data?.intervalCount
                                    )}
                                </span>
                                . After that, the regular annually membership
                                fee will apply.
                            </>
                        )}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        textAlign: "end",
                    }}
                >
                    <Button
                        className="btn-claim-offer"
                        variant="contained"
                        color="secondary"
                        sx={{
                            marginLeft: {
                                xs: "0",
                                md: "16px",
                            },
                            fontWeight: 500,
                            fontSize: "16px",
                            lineHeight: "19.98px",
                            letterSpacing: "0.01em",
                        }}
                        onClick={handleRedeemOffer}
                    >
                        REDEEM
                    </Button>
                </Box>
            </Box>
            <MembershipRedeemOfferModal
                ref={membershipRedeemOfferSuccesRef}
                data={subscriptionData as GetAvironSubscriptionQuery}
                isChoosingAnotherPlan={
                    isABTestCancelFollow && isChoosingAnotherPlan
                }
            />
        </Box>
    );
};

export default RedeemOffer;
